/**
 * Portfolio-monitoring template read/write contract (DOP-447..453).
 *
 * Source of truth for the JSON the `/api/monitoring` router serves. The
 * backend (DOP-447) and both UI pages (DOP-451 template page, DOP-452 run
 * page) build against THIS file; anything the server changes here is a
 * contract change and goes through the orchestrator.
 *
 * Parsed with Zod at the boundary (`zod/v3` import, matching `types/kpi.ts`).
 * Timestamps are epoch milliseconds; dates are ISO `YYYY-MM-DD` strings; ids
 * are public ids (never integer PKs); money is USD millions.
 */

import { z } from "zod/v3";

// ----------------------------------------------------------------- vocabularies

/** Where a ledger cell's value came from, on one run. */
export const MonitoringFigureStateSchema = z.enum([
	"found",
	"derived",
	"manual",
	"gap",
	"not_available",
]);
export type MonitoringFigureState = z.infer<typeof MonitoringFigureStateSchema>;

/** What a figure IS in the recipe. */
export const MonitoringFigureKindSchema = z.enum([
	"input",
	"derived",
	"covenant_required",
]);
export type MonitoringFigureKind = z.infer<typeof MonitoringFigureKindSchema>;

export const MonitoringUnitSchema = z.enum(["usd_m", "x", "pct"]);
export type MonitoringUnit = z.infer<typeof MonitoringUnitSchema>;

export const MonitoringRunStatusSchema = z.enum(["draft", "reviewed", "locked"]);
export type MonitoringRunStatus = z.infer<typeof MonitoringRunStatusSchema>;

export const MonitoringCovenantOutcomeSchema = z.enum([
	"pass",
	"near",
	"breach",
	"unknown",
]);
export type MonitoringCovenantOutcome = z.infer<
	typeof MonitoringCovenantOutcomeSchema
>;

export const MonitoringPackageStatusSchema = z.enum([
	"not_received",
	"partial",
	"received",
]);
export type MonitoringPackageStatus = z.infer<
	typeof MonitoringPackageStatusSchema
>;

/** Figure groups; the grid and the Summary sheet group rows this way. */
export const MonitoringFigureGroupSchema = z.enum([
	"financials",
	"leverage",
	"covenants",
	"adjustments",
]);
export type MonitoringFigureGroup = z.infer<typeof MonitoringFigureGroupSchema>;

// ----------------------------------------------------------------- recipe

export const MonitoringFigureSpecSchema = z.object({
	key: z.string(),
	label: z.string(),
	/**
	 * The parenthetical the label used to carry: "LTM", "certified" (DOP-458).
	 *
	 * Optional in the JSON, because a template stored before the split still
	 * serves a label of the form "Revenue (LTM)" and no qualifier at all — the
	 * Metrics page splits that trailing "(…)" off itself when this is null.
	 */
	qualifier: z.string().nullable().optional(),
	/**
	 * Whether the fund still asks a run for this figure (DOP-462).
	 *
	 * Optional and nullable, and read through `isFigureEnabled` rather than
	 * directly: a template stored before the flag existed serves no `enabled` at
	 * all, and dropping a metric out of the recipe because a field is absent
	 * would be exactly the wrong way round. Only an explicit `false` excludes.
	 */
	enabled: z.boolean().nullable().optional(),
	group: MonitoringFigureGroupSchema,
	kind: MonitoringFigureKindSchema,
	unit: MonitoringUnitSchema,
	required: z.boolean(),
	/** Document types searched, in order (input / covenant_required only). */
	search_order: z.array(z.string()),
	/** Human-readable formula over figure keys (derived only). */
	formula: z.string().nullable(),
	inputs: z.array(z.string()),
	definition: z.string().nullable(),
	sort_order: z.number(),
});
export type MonitoringFigureSpec = z.infer<typeof MonitoringFigureSpecSchema>;

/**
 * Is this figure part of the recipe? (DOP-462)
 *
 * The one place `enabled` is interpreted, so "no flag stored" and "flag stored
 * true" cannot come apart anywhere in the UI. The server applies the same rule.
 */
export const isFigureEnabled = (
	figure: Pick<MonitoringFigureSpec, "enabled">,
): boolean => figure.enabled !== false;

export const MonitoringCovenantSpecSchema = z.object({
	test_key: z.string(),
	label: z.string(),
	actual_key: z.string(),
	operator: z.enum(["<=", ">="]),
	required_key: z.string(),
	near_band: z.number(),
});
export type MonitoringCovenantSpec = z.infer<typeof MonitoringCovenantSpecSchema>;

export const MonitoringTemplateRefSchema = z.object({
	public_id: z.string(),
	name: z.string(),
	fund_name: z.string(),
});
export type MonitoringTemplateRef = z.infer<typeof MonitoringTemplateRefSchema>;

export const MonitoringTemplateSchema = MonitoringTemplateRefSchema.extend({
	kind: z.string(),
	cadence: z.string(),
	package_due_days: z.number(),
	internal_deadline_days: z.number(),
	missing_data_policy: z.enum(["run_and_flag", "refuse"]),
	reviewer_name: z.string().nullable(),
	/** The firm's own workbook the layout was seeded from, when one is stored. */
	layout: z.object({ file_id: z.string(), filename: z.string() }).nullable(),
	figure_spec: z.array(MonitoringFigureSpecSchema),
	covenant_spec: z.array(MonitoringCovenantSpecSchema),
	borrowers_count: z.number(),
	created_at: z.number(),
	updated_at: z.number(),
});
export type MonitoringTemplate = z.infer<typeof MonitoringTemplateSchema>;

/**
 * What the recipe does when a required figure cannot be found (DOP-456).
 * Derived from the schema rather than written out a second time, so the union
 * the UI switches on cannot drift from the one the parser accepts.
 */
export type MonitoringMissingDataPolicy =
	MonitoringTemplate["missing_data_policy"];

export const MonitoringBorrowerSchema = z.object({
	public_id: z.string(),
	name: z.string(),
	aliases: z.array(z.string()),
	sponsor: z.string().nullable(),
	sector: z.string().nullable(),
	sort_order: z.number(),
});
export type MonitoringBorrower = z.infer<typeof MonitoringBorrowerSchema>;

/**
 * A borrower name the user's own filenames already carry (DOP-454).
 *
 * The package resolver matches documents to a borrower by the filename prefix,
 * so the roster's spelling has to BE the corpus's spelling. These are the names
 * the server found, grouped by the folder they were found in, with whoever is
 * already on the roster removed.
 */
export const MonitoringBorrowerCandidateSchema = z.object({
	name: z.string(),
	/** How many of the user's documents start with this name. */
	file_count: z.number(),
});
export type MonitoringBorrowerCandidate = z.infer<
	typeof MonitoringBorrowerCandidateSchema
>;

export const MonitoringBorrowerCandidateGroupSchema = z.object({
	/** Null for documents in no folder at all; `folder_name` is then "Unfiled". */
	folder_id: z.number().nullable(),
	folder_name: z.string(),
	candidates: z.array(MonitoringBorrowerCandidateSchema),
});
export type MonitoringBorrowerCandidateGroup = z.infer<
	typeof MonitoringBorrowerCandidateGroupSchema
>;

export const MonitoringPeriodSchema = z.object({
	public_id: z.string(),
	label: z.string(),
	period_end: z.string(),
	package_due: z.string(),
	internal_deadline: z.string(),
});
export type MonitoringPeriod = z.infer<typeof MonitoringPeriodSchema>;

// ----------------------------------------------------------------- package

/** One document in (or considered for) a borrower's package. */
export const MonitoringPackageDocumentSchema = z.object({
	/** The id the file download route and `doppler://pdf/<file_id>` accept. */
	file_id: z.string(),
	filename: z.string(),
	doc_type: z.string(),
	/** False when another version of the same document type was preferred. */
	chosen: z.boolean(),
	chosen_over: z.array(z.string()),
});
export type MonitoringPackageDocument = z.infer<
	typeof MonitoringPackageDocumentSchema
>;

export const MonitoringPackageSchema = z.object({
	status: MonitoringPackageStatusSchema,
	/** Past the package due date without a complete package. */
	overdue: z.boolean(),
	documents: z.array(MonitoringPackageDocumentSchema),
});
export type MonitoringPackage = z.infer<typeof MonitoringPackageSchema>;

// ----------------------------------------------------------------- ledger

/** Where a found figure was read. `anchor` opens the PDF viewer at the page. */
export const MonitoringSourceRefSchema = z.object({
	file_id: z.string(),
	filename: z.string(),
	doc_type: z.string(),
	page: z.number().nullable(),
	quote: z.string().nullable(),
	anchor: z.string().nullable(),
});
export type MonitoringSourceRef = z.infer<typeof MonitoringSourceRefSchema>;

/**
 * The same figure one period earlier, carried on the figure itself (DOP-465).
 *
 * The prior column used to be assembled from the compare endpoint, which only
 * answers once an earlier period is LOCKED. That made last quarter's numbers a
 * reward for a workflow step rather than a fact about the borrower. The run now
 * states its own prior for every figure — read out of the earlier run when
 * there is one (`origin: "run"`), or off this period's package when the
 * document reports the comparative itself (`origin: "package"`).
 *
 * `.nullable().optional()`: a payload generated before the field existed still
 * parses, and reads as "no prior" rather than as a broken run.
 */
export const MonitoringFigurePriorSchema = z.object({
	value: z.number().nullable(),
	/**
	 * How the prior value was arrived at. The same vocabulary as a current
	 * cell's `state` — deliberately the whole enum and not the four the server
	 * emits today, so a fifth never turns a run into a parse error.
	 */
	state: MonitoringFigureStateSchema,
	source: MonitoringSourceRefSchema.nullable(),
	verified: z.boolean(),
	/**
	 * Which period's evidence it came from: an earlier run, or this package.
	 * Null when nothing answered (a gap prior has no origin).
	 */
	origin: z.enum(["run", "package"]).nullable(),
});
export type MonitoringFigurePrior = z.infer<typeof MonitoringFigurePriorSchema>;

export const MonitoringFigureSchema = z.object({
	key: z.string(),
	label: z.string(),
	group: MonitoringFigureGroupSchema,
	kind: MonitoringFigureKindSchema,
	state: MonitoringFigureStateSchema,
	value: z.number().nullable(),
	unit: MonitoringUnitSchema,
	/** found: the quote was re-read on the page. Dotted-amber when false. */
	verified: z.boolean(),
	source: MonitoringSourceRefSchema.nullable(),
	formula: z.string().nullable(),
	inputs: z.array(z.string()),
	gap_reason: z.string().nullable(),
	entered_by_name: z.string().nullable(),
	entered_at: z.number().nullable(),
	note: z.string().nullable(),
	/** The same figure, one period earlier (DOP-465). Absent → no prior. */
	prior: MonitoringFigurePriorSchema.nullable().optional(),
	sort_order: z.number(),
});
export type MonitoringFigure = z.infer<typeof MonitoringFigureSchema>;

export const MonitoringCovenantResultSchema = z.object({
	test_key: z.string(),
	label: z.string(),
	operator: z.enum(["<=", ">="]),
	unit: MonitoringUnitSchema,
	actual: z.number().nullable(),
	required: z.number().nullable(),
	/** required − actual for "<=", actual − required for ">=". */
	headroom: z.number().nullable(),
	result: MonitoringCovenantOutcomeSchema,
	actual_key: z.string(),
	required_key: z.string(),
	actual_state: MonitoringFigureStateSchema.nullable(),
	required_state: MonitoringFigureStateSchema.nullable(),
});
export type MonitoringCovenantResult = z.infer<
	typeof MonitoringCovenantResultSchema
>;

/** A source document with the figures read from it (Sources tab). */
export const MonitoringSourceDocumentSchema = z.object({
	file_id: z.string(),
	filename: z.string(),
	doc_type: z.string(),
	figure_keys: z.array(z.string()),
	pages: z.array(z.number()),
});
export type MonitoringSourceDocument = z.infer<
	typeof MonitoringSourceDocumentSchema
>;

export const MonitoringRunSummarySchema = z.object({
	public_id: z.string(),
	version: z.number(),
	status: MonitoringRunStatusSchema,
	/** True while a generation job is queued or running for this run. */
	generating: z.boolean(),
	gaps_open: z.number(),
	generated_at: z.number().nullable(),
	generation_error: z.string().nullable(),
	reviewed_at: z.number().nullable(),
	reviewed_by_name: z.string().nullable(),
	locked_at: z.number().nullable(),
	locked_by_name: z.string().nullable(),
});
export type MonitoringRunSummary = z.infer<typeof MonitoringRunSummarySchema>;

/**
 * Which period this run's prior column names (DOP-465).
 *
 * It used to be the prior LOCKED run, and everything on it — a run id, that
 * run's whole period record, its figures — was therefore guaranteed. A prior
 * period no longer implies a run: the values now ride on each figure
 * (`MonitoringFigurePriorSchema`) and can come off this period's own package,
 * in which case there is a period to name and nothing else. So only `period`,
 * and only its `label` and `period_end`, are relied on; the rest parses when
 * the server still sends it and is absent otherwise.
 */
export const MonitoringPriorSchema = z.object({
	run_public_id: z.string().nullable().optional(),
	period: z.object({
		label: z.string(),
		period_end: z.string(),
		public_id: z.string().optional(),
		package_due: z.string().optional(),
		internal_deadline: z.string().optional(),
	}),
	figures: z
		.record(
			z.object({
				value: z.number().nullable(),
				unit: MonitoringUnitSchema,
				state: MonitoringFigureStateSchema,
			}),
		)
		.optional(),
});
export type MonitoringPrior = z.infer<typeof MonitoringPriorSchema>;

export const MonitoringRunSchema = MonitoringRunSummarySchema.extend({
	template: MonitoringTemplateRefSchema,
	borrower: MonitoringBorrowerSchema,
	period: MonitoringPeriodSchema,
	package: z.array(MonitoringPackageDocumentSchema),
	lock_note: z.string().nullable(),
	partner_signoff_requested: z.boolean(),
	model: z.string().nullable(),
	cost_usd: z.number().nullable(),
	figures: z.array(MonitoringFigureSchema),
	covenants: z.array(MonitoringCovenantResultSchema),
	sources: z.array(MonitoringSourceDocumentSchema),
	prior: MonitoringPriorSchema.nullable(),
});
export type MonitoringRun = z.infer<typeof MonitoringRunSchema>;

export const MonitoringRunVersionSchema = z.object({
	public_id: z.string(),
	version: z.number(),
	status: MonitoringRunStatusSchema,
	generated_at: z.number().nullable(),
	generation_error: z.string().nullable(),
	reviewed_at: z.number().nullable(),
	locked_at: z.number().nullable(),
});
export type MonitoringRunVersion = z.infer<typeof MonitoringRunVersionSchema>;

// ----------------------------------------------------------------- board

export const MonitoringBoardRowSchema = z.object({
	borrower: MonitoringBorrowerSchema,
	package: MonitoringPackageSchema,
	run: MonitoringRunSummarySchema.nullable(),
	covenants: z.object({
		worst: MonitoringCovenantOutcomeSchema,
		results: z.array(MonitoringCovenantResultSchema),
	}),
});
export type MonitoringBoardRow = z.infer<typeof MonitoringBoardRowSchema>;

export const MonitoringBoardSchema = z.object({
	template: MonitoringTemplateRefSchema,
	period: MonitoringPeriodSchema,
	rows: z.array(MonitoringBoardRowSchema),
	counts: z.object({
		borrowers: z.number(),
		received: z.number(),
		partial: z.number(),
		not_received: z.number(),
		overdue: z.number(),
		draft: z.number(),
		reviewed: z.number(),
		locked: z.number(),
		gaps_open: z.number(),
	}),
});
export type MonitoringBoard = z.infer<typeof MonitoringBoardSchema>;

// ----------------------------------------------------------------- compare

export const MonitoringCompareSchema = z.object({
	current: z.object({ run_public_id: z.string(), period: MonitoringPeriodSchema }),
	prior: z.object({ run_public_id: z.string(), period: MonitoringPeriodSchema }),
	figures: z.array(
		z.object({
			key: z.string(),
			label: z.string(),
			group: MonitoringFigureGroupSchema,
			unit: MonitoringUnitSchema,
			prior: z.number().nullable(),
			current: z.number().nullable(),
			delta: z.number().nullable(),
			delta_pct: z.number().nullable(),
		}),
	),
	covenants: z.array(
		z.object({
			test_key: z.string(),
			label: z.string(),
			operator: z.enum(["<=", ">="]),
			unit: MonitoringUnitSchema,
			prior: MonitoringCovenantResultSchema.nullable(),
			current: MonitoringCovenantResultSchema,
			headroom_delta: z.number().nullable(),
		}),
	),
});
export type MonitoringCompare = z.infer<typeof MonitoringCompareSchema>;

// ----------------------------------------------------------------- writes

export const MonitoringRunRequestResultSchema = z.object({
	queued: z.array(
		z.object({ borrower_public_id: z.string(), run_public_id: z.string() }),
	),
	skipped: z.array(
		z.object({ borrower_public_id: z.string(), reason: z.string() }),
	),
});
export type MonitoringRunRequestResult = z.infer<
	typeof MonitoringRunRequestResultSchema
>;

export interface MonitoringCreateTemplatePayload {
	name: string;
	fund_name: string;
}

/** A partial edit of the recipe: only the fields present are changed (DOP-456). */
export interface MonitoringUpdateTemplatePayload {
	missing_data_policy?: MonitoringMissingDataPolicy;
	/**
	 * Include/exclude metrics by figure key (DOP-462). A PARTIAL map: the keys
	 * present are set, every other figure keeps the flag it has — so one
	 * checkbox click sends one entry and cannot overwrite a change made in
	 * another tab between the read and the write.
	 */
	figure_enabled?: Record<string, boolean>;
}

export interface MonitoringCreateBorrowerPayload {
	name: string;
	aliases?: string[];
	sponsor?: string | null;
	sector?: string | null;
}

export interface MonitoringCreatePeriodPayload {
	label: string;
	/** ISO date, the last day of the period. */
	period_end: string;
}

export interface MonitoringRunPeriodPayload {
	borrower_ids?: string[];
	only_received?: boolean;
}

/** Manual entry for a figure: a value, or acceptance that it is not available. */
export interface MonitoringSetFigurePayload {
	value?: number | null;
	note: string;
	accept_not_available?: boolean;
}

export interface MonitoringLockPayload {
	note?: string;
	partner_signoff_requested?: boolean;
}

// ----------------------------------------------------------------- parsing

export class MonitoringMalformedResponseError extends Error {
	constructor(what: string, issues: string) {
		super(`Malformed ${what} response: ${issues}`);
		this.name = "MonitoringMalformedResponseError";
	}
}

function parseWith<T>(schema: z.ZodType<T>, what: string, raw: unknown): T {
	const result = schema.safeParse(raw);
	if (!result.success) {
		throw new MonitoringMalformedResponseError(what, result.error.message);
	}
	return result.data;
}

export const parseMonitoringTemplate = (raw: unknown): MonitoringTemplate =>
	parseWith(MonitoringTemplateSchema, "template", raw);
export const parseMonitoringTemplateList = (raw: unknown): MonitoringTemplate[] =>
	parseWith(z.array(MonitoringTemplateSchema), "template list", raw);
export const parseMonitoringBorrowerList = (raw: unknown): MonitoringBorrower[] =>
	parseWith(z.array(MonitoringBorrowerSchema), "borrower list", raw);
export const parseMonitoringBorrower = (raw: unknown): MonitoringBorrower =>
	parseWith(MonitoringBorrowerSchema, "borrower", raw);
export const parseMonitoringBorrowerCandidateGroupList = (
	raw: unknown,
): MonitoringBorrowerCandidateGroup[] =>
	parseWith(
		z.array(MonitoringBorrowerCandidateGroupSchema),
		"borrower candidates",
		raw,
	);
export const parseMonitoringPeriodList = (raw: unknown): MonitoringPeriod[] =>
	parseWith(z.array(MonitoringPeriodSchema), "period list", raw);
export const parseMonitoringPeriod = (raw: unknown): MonitoringPeriod =>
	parseWith(MonitoringPeriodSchema, "period", raw);
export const parseMonitoringBoard = (raw: unknown): MonitoringBoard =>
	parseWith(MonitoringBoardSchema, "board", raw);
export const parseMonitoringRun = (raw: unknown): MonitoringRun =>
	parseWith(MonitoringRunSchema, "run", raw);
export const parseMonitoringRunVersionList = (
	raw: unknown,
): MonitoringRunVersion[] =>
	parseWith(z.array(MonitoringRunVersionSchema), "run versions", raw);
export const parseMonitoringCompare = (raw: unknown): MonitoringCompare =>
	parseWith(MonitoringCompareSchema, "compare", raw);
export const parseMonitoringRunRequestResult = (
	raw: unknown,
): MonitoringRunRequestResult =>
	parseWith(MonitoringRunRequestResultSchema, "run request", raw);

// ----------------------------------------------------------------- helpers

/** Figures with an open gap: the work list, and what blocks the lock. */
export const openGaps = (figures: MonitoringFigure[]): MonitoringFigure[] =>
	figures.filter((f) => f.state === "gap");

/** Worst-first ordering for covenant outcomes. */
export const COVENANT_OUTCOME_RANK: Record<MonitoringCovenantOutcome, number> = {
	breach: 0,
	near: 1,
	unknown: 2,
	pass: 3,
};
